/* Cartbot (the installed cart app) replaces window.fetch with its own wrapper,
   and that wrapper can throw SYNCHRONOUSLY — observed live as
   "TypeError: Cannot read properties of null (reading 'items')".

   A synchronous throw escapes the promise chain, so .catch()/.finally() never
   run. Every add path below has already preventDefault()ed the click and put
   the button into a "busy" state, so the throw leaves that button spinning for
   ever with no product and no error. Normalising it to a rejection lets the
   existing error handling do its job. See PR #107 for the same fix in
   component-last-chance.js. */
function safeFetch(url, options) {
  try {
    return fetch(url, options);
  } catch (err) {
    return Promise.reject(err);
  }
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.selectors = {
      closeButton: ".cart-drawer__close-button",
      cartLink: "[href='/cart']",
    };

    this.elements = {
      closeButton: this.querySelector(this.selectors.closeButton),
      cartLink: document.querySelector(this.selectors.cartLink),
    };

    this.init();

    // setTimeout(() => {
    //   this.toggle();
    // }, 2000);
  }

  init() {
    // bind close button event
    this.elements.closeButton.addEventListener("click", this.toggle.bind(this));

    // bind click outside drawer to close
    document.addEventListener('click', (event) => {

      // cart link click should open drawer and refresh content
      const isCartLinkClick = this.elements.cartLink && (this.elements.cartLink.contains(event.target) || this.elements.cartLink === event.target || !!event.target.closest(this.selectors.cartLink));

      if (isCartLinkClick) {
        event.preventDefault();
        event.stopPropagation();
        this.refresh();
        this.toggle();
        return;
      }

      // If click is outside drawer content
      if (!this.classList.contains('is-open')) return;
      const drawerContent = this.querySelector('.cart-drawer__content');
      const quickviewContent = this.querySelector('cart-item-quickview');
      if (!drawerContent.contains(event.target) && !quickviewContent.contains(event.target)) {
        this.toggle();
      }
    });

    // Listen for custom 'cart:updated' event to refresh cart drawer content
    this.addEventListener('cart:updated', (event) => {
      const cart = event?.detail?.cart;
      if (cart) {
        this._update(cart);
      } else {
        this.refresh();
      }
    });

    document.addEventListener('cart:open-drawer', (event) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      if (event?.detail?.cart) {
        this._update(event.detail.cart, this.toggle.bind(this));
      } else {
        this.toggle();
        this.refresh();
      }
    });

    document.addEventListener('cart:refresh-drawer', (event) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      this.refresh();
    });
  }

  async refresh() {
    this.classList.add("is-updating");
    try {
      const response = await fetch('/?section_id=cart-drawer');
      const html = await response.text();

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const newDrawer = doc.querySelector('cart-drawer');
      const currentDrawer = document.querySelector('cart-drawer');

      if (newDrawer && currentDrawer) {
        currentDrawer.querySelector('.cart-drawer__items').innerHTML = newDrawer.querySelector('.cart-drawer__items').innerHTML;
        currentDrawer.querySelector('.cart-drawer__item-count').innerHTML = newDrawer.querySelector('.cart-drawer__item-count')?.innerHTML || '';
        document.querySelectorAll('.cartCount').forEach(el => {
          el.innerHTML = newDrawer.querySelector('.cart-drawer__item-count').dataset.cartItemsCount || '0';
        });

        // cart-drawer__message update
        if (newDrawer.querySelector('.cart-drawer__message')) {
          const progressFillNew = newDrawer.querySelector('.cart-drawer__message .cart-bxgy-bar__progress-fill');
          let progressFillCurrent = currentDrawer.querySelector('.cart-drawer__message .cart-bxgy-bar__progress-fill');

          const messageCurrent = currentDrawer.querySelector('.cart-drawer__message .cart-bxgy-bar__message');
          const messageNew = newDrawer.querySelector('.cart-drawer__message .cart-bxgy-bar__message');
          if (progressFillCurrent && progressFillNew) {
            progressFillCurrent.style.width = progressFillNew.dataset.progress + '%';
            messageCurrent.innerHTML = messageNew.innerHTML;
          } else {
            currentDrawer.querySelector('.cart-drawer__message').innerHTML = newDrawer.querySelector('.cart-drawer__message').innerHTML;
            setTimeout(() => {
              // const progressFillNew = newDrawer.querySelector('.cart-drawer__message .cart-bxgy-bar__progress-fill');
              progressFillCurrent = currentDrawer.querySelector('.cart-drawer__message .cart-bxgy-bar__progress-fill');
              if (progressFillCurrent && progressFillNew) {
                progressFillCurrent.style.width = progressFillNew.dataset.progress + '%';
              }
            }, 100);
          }
        } else {
          currentDrawer.querySelector('.cart-drawer__message').innerHTML = '';
        }


        // footer update
        if (newDrawer.querySelector('.cart-drawer__footer')) {
          if (currentDrawer.querySelector('.cart-drawer__subtotal') && newDrawer.querySelector('.cart-drawer__subtotal')) {
            currentDrawer.querySelector('.cart-drawer__subtotal').innerHTML = newDrawer.querySelector('.cart-drawer__subtotal')?.innerHTML || '';
          } else {
            currentDrawer.querySelector('.cart-drawer__footer').innerHTML = newDrawer.querySelector('.cart-drawer__footer').innerHTML;
          }

          // The add-ons disclosure only renders when the cart's last non-addon
          // product carries addons metafields, so it can be present on one side
          // and absent on the other. Two bugs lived here: writing into a missing
          // one threw and left the drawer frozen (a cart holding only a tabletop),
          // and only ever swapping its contents meant a cart that became eligible
          // later never got the block at all, because the subtotal-only branch
          // above stops rebuilding the footer. Sync the whole <details> instead.
          const footer = currentDrawer.querySelector('.cart-drawer__footer');
          const currentDisclosure = currentDrawer.querySelector('.cart-drawer__upsell-disclosure');
          const newDisclosure = newDrawer.querySelector('.cart-drawer__upsell-disclosure');

          if (currentDisclosure && newDisclosure) {
            // Swap contents only, so the shopper's open/closed choice survives.
            const currentUpsell = currentDisclosure.querySelector('.cart-drawer__upsell');
            if (currentUpsell) {
              currentUpsell.innerHTML = newDisclosure.querySelector('.cart-drawer__upsell')?.innerHTML || '';
            }
          } else if (newDisclosure && footer) {
            // It renders first inside the footer. insertAdjacentHTML rather than
            // moving the parsed node, so the cart-upsell-item custom elements
            // upgrade on insert and the section's MutationObserver reapplies the
            // desktop open-by-default rule.
            footer.insertAdjacentHTML('afterbegin', newDisclosure.outerHTML);
          } else if (currentDisclosure) {
            // Nothing to upsell against this cart any more: drop the whole row
            // rather than leave an empty "Explore add-ons" toggle behind.
            currentDisclosure.remove();
          }
        } else {
          currentDrawer.querySelector('.cart-drawer__footer').innerHTML = '';
        }

        const cartBarFill = currentDrawer.querySelectorAll('.cart-bxgy-bar__progress-fill');
        // cartBarFill.forEach(bar => {
        //   console.log('Updating cart progress bar:', bar.dataset.progress);
        //   const progress = bar.dataset.progress;
        //   setTimeout(() => {
        //   bar.style.width = `${progress}%`;
        //   }, 100);
        // });
      }

    } catch (error) {
      console.error('Cart drawer refresh failed:', error);
    } finally {
      // Always clear the updating state. The overlay it drives sets
      // pointer-events: none on both the item list and the footer, so anything
      // that throws above locks the customer out of their own cart and the
      // checkout button until they reload the page.
      this.classList.remove("is-updating");
    }
  }

  async _update(cart, cb = null) {
    const bxgyScripts = this.querySelectorAll('.bxgy-script');
    const itemsToAdd = [];
    const itemsToRemove = [];

    bxgyScripts.forEach(script => {
      const variantId = script.dataset.variantId;
      const threshold = Number(script.dataset.threshold);
      if (!variantId || !threshold) return;

      const isInCart = cart.items.some(item => item.variant_id == variantId);
      const thresholdMet = cart.items_subtotal_price >= threshold * 100;

      if (thresholdMet && !isInCart) {
        itemsToAdd.push({
          id: variantId,
          quantity: 1,
          properties: {
            "_bxgy_threshold": threshold
          }
        });
      } else if (!thresholdMet && isInCart) {
        itemsToRemove.push(variantId);
      }
    });

    if (itemsToAdd.length === 0 && itemsToRemove.length === 0) {
      this.refresh();
      if (cb) cb();
      return;
    }

    try {
      for (const variantId of itemsToRemove) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 0 })
        }).then(r => r.json());
      }


      if (itemsToAdd.length > 0) {
        const addResponse = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' },
          body: JSON.stringify({ items: itemsToAdd })
        }).then(r => r.json());

        if (addResponse.status) {
          // Add failed (e.g. out of stock), still refresh
        }
      }
    } catch (e) {
      console.error(e);
    }

    this.refresh();
    if (cb) cb();
  }

  toggle() {
    if (this.classList.contains('is-open')) {
      document.body.classList.remove('cart-drawer-open');
      document.documentElement.classList.remove('cart-drawer-open');
      // document.html.style.overflow = '';
    } else {
      document.body.classList.add('cart-drawer-open');
      document.documentElement.classList.add('cart-drawer-open');
      // document.html.style.overflow = 'hidden';
    }

    this.classList.toggle("is-open");
  }
}

customElements.define("cart-drawer", CartDrawer);

class CartItem extends HTMLElement {
  constructor() {
    super();
    this.selectors = {
      removeItemBtn: ".cart-drawer__item__remove",
      quantityInput: "quantity-input"
    };

    this.elements = {
      removeItemBtn: this.querySelector(this.selectors.removeItemBtn),
      quantityInput: this.querySelector(this.selectors.quantityInput),
      cartDrawer: this.closest('cart-drawer')
    };

    this.init();
  }

  init() {
    const removeBtn = this.elements.removeItemBtn;
    if (removeBtn) {
      removeBtn.addEventListener("click", () => {
        const lineId = removeBtn.dataset.cartItemId;
        this.removeItem(lineId);
      });
    }

    const quantityInput = this.elements.quantityInput;
    if (quantityInput) {
      quantityInput.addEventListener("change", () => {
        const lineId = quantityInput.dataset.cartItemId;
        const quantity = parseInt(quantityInput.querySelector("input[type='number']").value);
        this.updateItemQuantity(lineId, quantity);
      });
    }
  }

  async updateItemQuantity(lineId, quantity) {
    this.elements.cartDrawer.classList.add("is-updating");
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: lineId,
          quantity: quantity
        })
      });

      if (!response.ok) throw new Error('Failed to update cart');

      const cart = await response.json();

      // Re-render your slideout cart UI here
      this.updateCart(cart);



    } catch (error) {
      console.error('Cart update error:', error);
      // updateCart() -> 'cart:updated' -> refresh() only runs on the success path,
      // so the overlay has to be cleared here or a failed quantity change leaves
      // the drawer frozen the same way.
      this.elements.cartDrawer.classList.remove("is-updating");
    }
  }

  async removeItem(lineId) {
    await this.updateItemQuantity(lineId, 0);
  }

  updateCart(cart) {
    // Dispatch custom event to notify cart drawer to refresh
    this.elements.cartDrawer.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart }, bubbles: true }));
  }

  toggle() {
    this.classList.toggle("is-open");
  }
}

customElements.define("cart-item", CartItem);

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.selectors = {
      input: "input[type='number']",
      plusBtn: ".cart-drawer__quantity-plus",
      minusBtn: ".cart-drawer__quantity-minus",
    };

    this.elements = {
      input: this.querySelector(this.selectors.input),
      plusBtn: this.querySelector(this.selectors.plusBtn),
      minusBtn: this.querySelector(this.selectors.minusBtn),
    };

    this.init();
  }

  init() {
    // bind plus button event
    this.elements.plusBtn.addEventListener("click", this.onPlus.bind(this));

    // bind minus button event
    this.elements.minusBtn.addEventListener("click", this.onMinus.bind(this));
  }

  onChange() {
    const changeEvent = new Event("change", { bubbles: true });
    const inputEvent = new Event("input", { bubbles: true });

    this.elements.input.dispatchEvent(changeEvent);
    this.elements.input.dispatchEvent(inputEvent);
  }

  onPlus(event) {
    this.elements.input.value = parseInt(this.elements.input.value) + 1;
    this.onChange();
  }

  onMinus(event) {
    const min = parseInt(this.elements.input.min) + 1 || 1;
    const newValue = Math.max(min, parseInt(this.elements.input.value) - 1);
    if (parseInt(this.elements.input.value) === newValue) return;
    this.elements.input.value = newValue;
    this.onChange();
  }
}

customElements.define("quantity-input", QuantityInput);

class CartUpsellItem extends HTMLElement {
  constructor() {
    super();
    this.selectors = {
      quickviewBtn: ".cart-drawer__item-quickview-btn"
    };

    this.elements = {
      quickviewBtn: this.querySelector(this.selectors.quickviewBtn),
      cartDrawer: this.closest('cart-drawer')
    };

    this.init();
  }

  /* 1-based index among the row's cards, matching listPosition on the other two
     surfaces. */
  get trackFields() {
    const row = this.parentNode ? Array.prototype.slice.call(this.parentNode.querySelectorAll('cart-upsell-item')) : [];
    return {
      surface: (window.mgUpsell && window.mgUpsell.SURFACE.CART) || 'cart_addons',
      handle: this.dataset.productHandle || '',
      position: row.indexOf(this) + 1
    };
  }

  init() {
    // Funnel tracking, shared with the PDP carousel and the checkout modal so the
    // three can be compared. The drawer re-renders this row after every cart
    // change; observe() is idempotent per element, so a card cannot count twice.
    if (window.mgUpsell) window.mgUpsell.observe(this, this.trackFields);

    // Open the quick-view popup from the + button AND from the whole image box.
    const openQuickview = () => {
      if (window.mgUpsell) window.mgUpsell.click(this.trackFields);
      const productJsonString = this.querySelector('.cart-upsell__item-data').innerHTML;
      const productOptionsJsonString = this.querySelector('.cart-upsell__item-product-options').innerHTML;
      const productData = JSON.parse(productJsonString);
      const productOptionsData = JSON.parse(productOptionsJsonString);
      productData.options_with_values = productOptionsData;
      // Dispatch custom event to open quick view modal with product handle
      document.dispatchEvent(new CustomEvent('cart-upsell:open-quickview', {
        detail: { product: productData, trackFields: this.trackFields },
        bubbles: true
      }));
    };

    const quickviewBtn = this.elements.quickviewBtn;
    if (quickviewBtn) {
      quickviewBtn.addEventListener("click", openQuickview);
    }

    // Whole image box is a click target for the popup, same as the + button.
    const image = this.querySelector('img');
    if (image) {
      image.style.cursor = 'pointer';
      image.addEventListener('click', openQuickview);
    }
  }
}

customElements.define("cart-upsell-item", CartUpsellItem);

class CartItemQuickview extends HTMLElement {
  constructor() {
    super();
    this.selectors = {
      closeButton: ".quickview-close",
    };

    this.elements = {
      closeButton: this.querySelector(this.selectors.closeButton),
    };

    this.init();
  }

  init() {
    // bind close button event
    if (this.elements.closeButton) {
      this.elements.closeButton.addEventListener("click", this.close.bind(this));
    }

    // Listen for custom 'cart-upsell:open-quickview' event to open quick view modal with product data
    document.addEventListener('cart-upsell:open-quickview', (event) => {
      if (event.detail && event.detail.product) {
        this.open(event.detail.product, event.detail);
      }
    });
  }

  close() {
    this.classList.remove('is-open');
  }

  open(product, detail = {}) {
    this.classList.add('is-open');
    // You can add logic here to populate the quick view with product data

    const template = document.getElementById('cart-upsell__item-quickview-template').innerHTML;

    // First AVAILABLE variant, not variants[0]. The radios below pre-check
    // whatever this resolves to, so the two can never disagree — and a product
    // whose first variant is sold out no longer stages a sold-out variant.
    // /cart/add.js is all-or-nothing, so one rejected line takes the whole
    // batch down with it, including the main product.
    const variant = product.variants.find((v) => v.available) || product.variants[0];

    const cartUpsellItem = document.querySelector(`cart-upsell-item[data-product-id="${product.id}"]`);

    // Prefer the discount the CALLER passed. Looking it up by product id finds
    // the first matching element in the document, which on a page showing both
    // the drawer upsell and another offer for the same product is the wrong one
    // — it would price the item at the other surface's percentage.
    const discountAmount =
      detail.discountAmount != null
        ? Number(detail.discountAmount) || 0
        : cartUpsellItem
          ? Number(cartUpsellItem.dataset.discountAmount) || 0
          : 0;

    const badge_message =
      detail.badge != null
        ? detail.badge
        : cartUpsellItem?.querySelector('.cart-upsell__item__message')?.innerHTML || '';

    // Held for productOptionsEvents and initProductForm, which used to re-run
    // the same ambiguous DOM lookup on every swatch change.
    this._discountAmount = discountAmount;
    this._productId = product.id;

    // Line-item properties the CALLER wants on whatever gets added. The drawer's
    // own upsell passes none and keeps the plain `addon: yes`; the last-chance
    // modal passes its claim marker, which is what lets the discount Function
    // tell an offer add apart from an ordinary one.
    this._extraProperties = detail.properties || null;

    // Which surface opened us. The quick view is shared between the drawer's
    // upsell row and the Check Out Exclusives modal, so an add fired from here
    // has to say which one it belongs to or the comparison is meaningless.
    this._trackFields = detail.trackFields || null;

    const variantOptions = this.productOptionsMarkup(product, variant);

    let qv = template.replaceAll(/{title}/g, product.title);
    qv = qv.replaceAll(/{badge}/g, badge_message);
    qv = qv.replaceAll(/{description}/g, product.description);
    qv = qv.replaceAll(/{price}/g, this.priceMarkup(variant, discountAmount));
    qv = qv.replaceAll(/{gallery}/g, this.galleryMarkup(product, variant));
    qv = qv.replaceAll(/{variants}/g, variantOptions);
    qv = qv.replaceAll(/{variant_id}/g, variant.id);
    qv = qv.replaceAll(/{product_id}/g, product.id);
    qv = qv.replaceAll(/{product_type}/g, product.type);
    qv = qv.replaceAll(/{url}/g, product.url || `/products/${product.handle}`);
    this.querySelector('.cart-item-quickview__content .quickview-body').innerHTML = qv;

    setTimeout(() => {
      this.productOptionsEvents(product);
      this.initProductForm();
    }, 100);
  }

  priceMarkup(variant, discountAmount) {
    if (discountAmount > 0) {
      return `
        <span class="cart-item-quickview__price--original">${Shopify.formatMoney(variant.price, window.cart_upsell.money_format)}</span>
        <span class="cart-item-quickview__price--sale">${Shopify.formatMoney(variant.price - discountAmount, window.cart_upsell.money_format)}</span>
      `;
    }

    if (variant.compare_at_price > variant.price) {
      return `
        <span class="cart-item-quickview__price--original">${Shopify.formatMoney(variant.compare_at_price, window.cart_upsell.money_format)}</span>
        <span class="cart-item-quickview__price--sale">${Shopify.formatMoney(variant.price, window.cart_upsell.money_format)}</span>
      `;
    } else {
      return `<span class="cart-item-quickview__price">${Shopify.formatMoney(variant.price, window.cart_upsell.money_format)}</span>`;
    }
  }

  // Takes the variant explicitly rather than assuming variants[0]. The popup
  // used to open on the first variant's photo while the radios pre-checked the
  // first option value — 8 of 21 add-ons opened showing a colour the shopper
  // had not selected (same defect fixed on the PDP in PR #101).
  galleryMarkup(product, variant) {
    const shown = variant || product.variants.find((v) => v.available) || product.variants[0];
    return `<div class="cart-item-quickview__gallery-item">
      <img src="${escapeAttr(shown?.featured_image?.src ?? product.featured_image)}" alt="${escapeAttr(product.title || 'Product Image')}" />
    </div>`;
  }

  productOptionsMarkup(product, selectedVariant) {
    if (!product.options_with_values || product.options_with_values.length === 0 || product.variants.length <= 1) return '';

    const chosen = selectedVariant || product.variants.find((v) => v.available) || product.variants[0];

    return `
      <div class="cart-item-quickview__options">
        ${product.options_with_values.map((option, optionIndex) => {
      const optionMarkup = `
            <div class="cart-item-quickview-option__selector__swatches">
              ${option.values.map((value, valueIndex) => {
        const isColor = option.name.toLowerCase() === 'color' || option.name.toLowerCase() === 'colour';

        return `
                  <div class="cart-item-quickview-option__selector__swatch ${isColor ? 'cart-item-quickview-option__selector__swatch--color' : ''}">
                    <input
                      type="radio"
                      value="${escapeAttr(value)}"
                      name="qv-opt-${product.id}-${handlize(option.name)}"
                      id="qv-option-${product.id}-${optionIndex}-${handlize(value)}"
                      ${chosen && chosen.options[optionIndex] === value ? 'checked' : ''}
                    />
                    <label
                      for="qv-option-${product.id}-${optionIndex}-${handlize(value)}"
                    >
                      ${isColor ? `<span style="background-color: ${escapeAttr(value.split(' ').slice(-1)[0].toLowerCase())}; background-image: url(${escapeAttr(this.fileUrl(`${handlize(value)}.png`))});">${escapeHtml(value)}</span>` : escapeHtml(value)}
                    </label>
                  </div>
                `;
      }).join('')}
            </div>
          `;;

      return `
            <div class="cart-item-quickview__option">
              <label>${option.name}</label>
              ${optionMarkup}
            </div>
          `;
    }).join('')}
      </div>
    `;
  }

  fileUrl(fileName) {
    if (!fileName || !this.dataset.fileUrl) return '';

    return this.dataset.fileUrl.replace('[file_image.jpg]', fileName) || '';
  }

  productOptionsEvents(product) {
    const optionSelectors = this.querySelectorAll('.cart-item-quickview-option__selector__swatch input[type="radio"]');
    const cartUpsellItem = document.querySelector(`cart-upsell-item[data-product-id="${product.id}"]`);
    optionSelectors.forEach(selector => {
      selector.addEventListener('change', (event) => {
        const selectedOptions = {};
        optionSelectors.forEach(sel => {
          if (sel.checked) {
            const optionName = sel.name.replace(`qv-opt-${product.id}-`, '').replace(/-/g, ' ');
            selectedOptions[optionName] = sel.value;
          }
        });

        const matchingVariant = product.variants.find(variant => {
          return Object.entries(selectedOptions).every(([optionName, optionValue]) => {
            const optionIndex = product.options.findIndex(opt => opt.toLowerCase() === optionName.toLowerCase());
            return variant.options[optionIndex] === optionValue;
          });
        });

        if (matchingVariant) {
          // Update price and gallery based on selected variant. Uses the
          // discount resolved in open(), not a fresh DOM lookup — see the note
          // there about which element the lookup actually finds.
          const discountAmount =
            this._discountAmount != null
              ? this._discountAmount
              : cartUpsellItem
                ? Number(cartUpsellItem.dataset.discountAmount) || 0
                : 0;
          this.querySelector('.quickview-price').innerHTML = this.priceMarkup(matchingVariant, discountAmount);
          this.querySelector('.quickview-variant-id').value = matchingVariant.id;
          this.querySelector('.quickview-gallery').innerHTML = this.galleryMarkup(product, matchingVariant);
        }
      });
    });
  }

  initProductForm() {
    const form = this.querySelector('.quickview-form');
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const addToCartButton = form.querySelector('input[type="submit"]');
      addToCartButton.classList.add("is-adding");
      const formData = this.serializeForm(form);
      // You can add logic here to add the product to cart using AJAX and then refresh the cart drawer
      const items = [
        {
          ...formData,
          properties: {
            "addon": "yes",
            ...(this._extraProperties || {})
          }
        }
      ];

      console.log('Adding to cart:', items);

      const config = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'Accept': `application/json` }
      };
      config.body = JSON.stringify({ items });

      safeFetch(`${form.getAttribute('action')}.js`, config)
        .then((response) => response.json())
        .then((response) => {
          // /cart/add.js answers {status: 422, description} on rejection. This
          // used to return silently, so a shopper who hit a sold-out variant
          // saw absolutely nothing happen. Tell whoever opened us.
          if (response.status) {
            document.dispatchEvent(new CustomEvent('quickview:add-failed', {
              bubbles: true,
              detail: {
                productId: this._productId,
                message: response.description || response.message || ''
              }
            }));
            return;
          }

          if (window.mgUpsell && this._trackFields) {
            window.mgUpsell.add(this._trackFields, { path: 'quickview' });
          }

          document.dispatchEvent(new CustomEvent('quickview:added', {
            bubbles: true,
            detail: { productId: this._productId, items }
          }));

          this.close();
          document.dispatchEvent(new CustomEvent('cart:refresh-drawer', { bubbles: true }));
        })
        .catch((e) => {
          console.error(e);
          document.dispatchEvent(new CustomEvent('quickview:add-failed', {
            bubbles: true,
            detail: { productId: this._productId, message: '' }
          }));
        })
        .finally(() => {
          addToCartButton.classList.remove("is-adding");
        });
    });
  }

  serializeForm(form) {
    var obj = {};
    var formData = new FormData(form);
    for (var key of formData.keys()) {
      if (key.includes('opt-') || key.includes('form_type') || key.includes('utf8')) {
        continue;
      }
      const value = key === 'quantity' ? Number(formData.get(key)) : formData.get(key);
      obj[key] = value;
    }
    return obj;
  };
}

customElements.define("cart-item-quickview", CartItemQuickview);

// Option values reach the markup through a template string, so a value with a
// double quote in it ends the attribute early. Desk sizes are written with inch
// marks — value="(40" x 24")" truncates to "(40", findVariantFromOptions never
// matches, and the caller silently keeps the default variant. That shipped the
// wrong desk for seven months before PR #102 fixed the Liquid side; this is the
// same defect on the JS side.
function escapeAttr(value) {
  if (value == null) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function escapeHtml(value) {
  if (value == null) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function handlize(str) {
  const handle = str
    .toLowerCase()
    .split('/') // split on slash
    .map((part, index) => {
      let p = part
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "") // remove accents
        .replace(/[^A-Za-z0-9\s-]/g, "")  // remove special characters
        .trim()
        .replace(/\s+/g, "-")             // spaces to hyphens
        .replace(/-+/g, "-");             // collapse hyphens

      return index === 0 ? p.toLowerCase() : p; // only lowercase first part
    })
    .join("-");

  return handle;
}
